import { Link } from "react-router-dom";
import type { ReactNode } from "react";
import { ScrollReveal } from "./ScrollReveal.tsx";

export function CTAButton({
  to,
  children,
  variant = "primary",
}: {
  to: string;
  children: ReactNode;
  variant?: "primary" | "secondary";
}) {
  const cls =
    variant === "primary"
      ? "bg-pine text-white shadow-soft hover:-translate-y-0.5 hover:bg-charcoal hover:shadow-xl"
      : "bg-white/90 text-pine ring-1 ring-pine/15 hover:-translate-y-0.5 hover:bg-sage hover:shadow-soft";
  return (
    <Link
      className={`inline-flex min-h-12 items-center justify-center rounded-full px-6 py-3 text-sm font-semibold transition duration-300 ${cls}`}
      to={to}
    >
      {children}
    </Link>
  );
}

export function Section({
  eyebrow,
  title,
  intro,
  children,
  className = "",
}: {
  eyebrow?: string;
  title: string;
  intro?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={`mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 ${className}`}
    >
      <ScrollReveal className="max-w-3xl">
        {eyebrow && (
          <p className="mb-3 text-sm font-semibold uppercase tracking-wide text-nordic">
            {eyebrow}
          </p>
        )}
        <h2 className="text-4xl font-semibold tracking-tight text-charcoal sm:text-5xl">
          {title}
        </h2>
        {intro && (
          <p className="mt-4 text-lg leading-8 text-charcoal/70">{intro}</p>
        )}
      </ScrollReveal>
      <ScrollReveal className="mt-10" delay={120}>
        {children}
      </ScrollReveal>
    </section>
  );
}

export function Card({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`group rounded-[2rem] border border-charcoal/10 bg-white/80 p-6 shadow-soft backdrop-blur transition duration-300 hover:-translate-y-1 hover:border-pine/20 hover:shadow-xl ${className}`}
    >
      {children}
    </div>
  );
}

export function FormError({ message }: { message?: string }) {
  return message ? (
    <p className="mt-2 text-sm text-red-700" role="alert">
      {message}
    </p>
  ) : null;
}

export function LoadingState({ label = "Loading" }: { label?: string }) {
  return (
    <div className="flex min-h-64 items-center justify-center text-charcoal/60">
      {label}…
    </div>
  );
}

export function EmptyState({ label }: { label: string }) {
  return (
    <div className="rounded-3xl border border-dashed border-charcoal/20 bg-white p-8 text-center text-charcoal/60">
      {label}
    </div>
  );
}

export function ToastNotification({
  message,
  tone = "success",
}: {
  message: string;
  tone?: "success" | "error";
}) {
  return (
    <div
      className={`rounded-2xl p-4 text-sm font-medium ${tone === "success" ? "bg-sage text-pine" : "bg-red-50 text-red-700"}`}
    >
      {message}
    </div>
  );
}
